import type {
	TopologyOwnerRuntimeHostFactoryOptions,
} from '../../../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeHostFactory';
import type {
	TopologyEdgeRelocationCandidate,
} from './TopologyEdgeRelocationService';
import type {TopologyOwnerRuntimeHost} from '../../../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeResolver';
import type {
	TopologyFaceRelocationCandidate,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';

/**
 * Scene 与历史 checkpoint 共用的 topology owner host 工厂。
 *
 * 本类只把无 Mesh topology 适配为边、面重定位候选；
 * 稳定身份和成员协调的最终选择仍由对应 relocation/reconcile 服务决定。
 */
export class TopologyOwnerRuntimeHostFactory {
	/**
	 * 从 topology snapshot 创建可枚举的 owner host。
	 *
	 * @param options topology、坐标转换与可选 Scene 边代理。
	 * @returns 可供 Convert Entities 重定位使用的 owner host。
	 */
	public create(
		options: TopologyOwnerRuntimeHostFactoryOptions,
	): TopologyOwnerRuntimeHost {
		const vertexByIndex = new Map(
			options.topology.vertices.map((vertex) => [vertex.index, vertex]),
		);
		const faceByIndex = new Map(
			options.topology.faces.map((face) => [face.index, face]),
		);
		const edgeByIndex = new Map(
			options.topology.edges.map((edge) => [edge.index, edge]),
		);

		return {
			getEdgeByIndex: (edgeIndex) => this.createCandidate(
				edgeByIndex.get(edgeIndex),
				vertexByIndex,
				faceByIndex,
				options,
			),
			listEdges: () => options.topology.edges
				.map((edge) => this.createCandidate(
					edge,
					vertexByIndex,
					faceByIndex,
					options,
				))
				.filter((candidate): candidate is TopologyEdgeRelocationCandidate => (
					Boolean(candidate)
				)),
			getFaceByIndex: (faceIndex) => this.createFaceCandidate(
				faceByIndex.get(faceIndex),
				edgeByIndex,
				vertexByIndex,
				faceByIndex,
				options,
			),
			listFaces: () => options.topology.faces
				.map((face) => this.createFaceCandidate(
					face,
					edgeByIndex,
					vertexByIndex,
					faceByIndex,
					options,
				))
				.filter((face): face is TopologyFaceRelocationCandidate => Boolean(face)),
		};
	}

	/**
	 * 把同一源实体血缘下的多个 topology host 组合为一个候选域。
	 *
	 * 切除可能把一个实体分裂为多个结果体。组合层允许重定位服务在这些合法成员间选择，
	 * 但索引在多个实体中并不唯一，因此只有单一成员命中时才允许索引快速读取。
	 *
	 * @param hosts 已在 owner 与实体血缘层完成过滤的 host 集合。
	 * @returns 仅枚举指定血缘成员的组合 host。
	 */
	public createCollection(
		hosts: readonly TopologyOwnerRuntimeHost[],
	): TopologyOwnerRuntimeHost {
		return {
			getEdgeByIndex: edgeIndex => {
				const candidates = hosts
					.map(host => host.getEdgeByIndex(edgeIndex))
					.filter((candidate): candidate is TopologyEdgeRelocationCandidate => Boolean(candidate));
				return candidates.length === 1 ? candidates[0] : null;
			},
			listEdges: () => hosts.flatMap(host => host.listEdges()),
			getFaceByIndex: faceIndex => {
				const candidates = hosts
					.map(host => host.getFaceByIndex?.(faceIndex) ?? null)
					.filter((candidate): candidate is TopologyFaceRelocationCandidate => Boolean(candidate));
				return candidates.length === 1 ? candidates[0] : null;
			},
			listFaces: () => hosts.flatMap(host => host.listFaces?.() ?? []),
		};
	}

	/**
	 * 把 topology face 快照转为面级重定位候选。
	 *
	 * @param face 当前面快照。
	 * @returns 不携带 Mesh 的面候选。
	 */
	private createFaceCandidate(
		face: RuntimeTopologyFace | undefined,
		edgeByIndex: ReadonlyMap<number, RuntimeTopologyEdge>,
		vertexByIndex: ReadonlyMap<number, RuntimeTopologyVertex>,
		faceByIndex: ReadonlyMap<number, RuntimeTopologyFace>,
		options: TopologyOwnerRuntimeHostFactoryOptions,
	): TopologyFaceRelocationCandidate | null {
		if (!face) {
			return null;
		}

		return {
			faceIndex: face.index,
			topologyMemberId: options.topologyMemberId,
			semanticName: face.semanticName,
			persistentRef: face.ref,
			subEntityId: face.subEntityId,
			surfaceKind: face.surfaceKind,
			owningFeatureId: face.owningFeatureId,
			adjacentEdgeIndices: [...face.adjacentEdgeIndices],
			boundaryEdges: face.adjacentEdgeIndices
				.map((edgeIndex) => this.createCandidate(
					edgeByIndex.get(edgeIndex),
					vertexByIndex,
					faceByIndex,
					options,
				))
				.filter((candidate): candidate is TopologyEdgeRelocationCandidate => (
					Boolean(candidate)
				)),
		};
	}

	/**
	 * 把单条 topology edge 转换为重定位候选。
	 *
	 * @param edge 当前边快照。
	 * @param vertexByIndex 顶点索引。
	 * @param faceByIndex 面索引。
	 * @param options 当前 host 构建选项。
	 * @returns 完整候选；边不存在时返回 `null`。
	 */
	private createCandidate(
		edge: RuntimeTopologyEdge | undefined,
		vertexByIndex: ReadonlyMap<number, RuntimeTopologyVertex>,
		faceByIndex: ReadonlyMap<number, RuntimeTopologyFace>,
		options: TopologyOwnerRuntimeHostFactoryOptions,
	): TopologyEdgeRelocationCandidate | null {
		if (!edge) {
			return null;
		}

		const featureLine = options.resolveFeatureLine?.(edge.index) ?? null;
		const endpoints = featureLine?.getWorldEndpoints() ?? null;
		const startVertex = vertexByIndex.get(edge.vertexAIndex) ?? null;
		const endVertex = vertexByIndex.get(edge.vertexBIndex) ?? null;
		const startWorld = startVertex
			? this.transformPoint(startVertex.position, options)
			: null;
		const endWorld = endVertex
			? this.transformPoint(endVertex.position, options)
			: null;
		const sampledPointWorlds = edge.sampledPoints
			?.filter((point) => Array.isArray(point) && point.length >= 3)
			.map((point) => this.transformPoint(point, options));
		const startAnchorRef = this.resolveVertexAnchorRef(startVertex);
		const endAnchorRef = this.resolveVertexAnchorRef(endVertex);
		const conic = edge.exactConic;
		const center = conic ? this.transformPoint(conic.center, options) : null;
		// 以两个轴端点做相同仿射变换，随后减中心；平移不会污染方向，缩放和镜像不会丢失。
		const xTip = conic ? this.transformPoint([
			conic.center[0] + conic.xVector[0], conic.center[1] + conic.xVector[1], conic.center[2] + conic.xVector[2],
		], options) : null;
		const yTip = conic ? this.transformPoint([
			conic.center[0] + conic.yVector[0], conic.center[1] + conic.yVector[1], conic.center[2] + conic.yVector[2],
		], options) : null;

		return {
			edgeIndex: edge.index,
			toWorldPoint: options.transformPoint,
			geometrySource: options.topology.geometrySource,
			...(conic && center && xTip && yTip ? {exactConic: {
				...conic, center,
				xVector: [xTip[0] - center[0], xTip[1] - center[1], xTip[2] - center[2]] as TopologyPoint3D,
				yVector: [yTip[0] - center[0], yTip[1] - center[1], yTip[2] - center[2]] as TopologyPoint3D,
			}} : {}),
			topologyMemberId: options.topologyMemberId,
			semanticName: edge.semanticName,
			curveKind: edge.curveKind,
			persistentRef: edge.ref,
			subEntityId: edge.subEntityId,
			owningFaceRefs: [...edge.owningFaceRefs],
			adjacentFaceSubEntityIds: edge.adjacentFaceIndices
				.map((faceIndex) => faceByIndex.get(faceIndex)?.subEntityId)
				.filter((value, index, values): value is string => (
					typeof value === 'string'
					&& value.length > 0
					&& values.indexOf(value) === index
				)),
			featureLine: featureLine ?? undefined,
			snapshot: edge,
			sampledPointWorlds: sampledPointWorlds && sampledPointWorlds.length >= 2
				? sampledPointWorlds
				: undefined,
			endpointAnchorRefs: startAnchorRef || endAnchorRef
				? {
					start: startAnchorRef,
					end: endAnchorRef,
				}
				: undefined,
			/*
			 * FeatureCurve 也继承 FeatureLine，并同样能返回首尾点。
			 * 只有 Puppy CAD 内核明确判定为直线时，这两个点才是可消费的线段载荷；
			 * 圆弧、圆和样条的首尾点只是弦端点，不能伪装成 worldLine。
			 */
			worldLine: edge.curveKind === 'line' && endpoints
				? {
					start: [endpoints.start.x, endpoints.start.y, endpoints.start.z],
					end: [endpoints.end.x, endpoints.end.y, endpoints.end.z],
				}
				: edge.curveKind === 'line' && startWorld && endWorld
					? {
						start: startWorld,
						end: endWorld,
					}
					: undefined,
		};
	}

	/**
	 * 将 snapshot 点转换到引用投影使用的坐标。
	 *
	 * @param point snapshot 点。
	 * @param options 当前 host 构建选项。
	 * @returns 独立坐标副本。
	 */
	private transformPoint(
		point: TopologyPoint3D,
		options: TopologyOwnerRuntimeHostFactoryOptions,
	): TopologyPoint3D {
		return options.transformPoint
			? options.transformPoint(point)
			: [point[0], point[1], point[2]];
	}

	/**
	 * 读取真实 topology 顶点的稳定锚点。
	 *
	 * @param vertex 顶点快照。
	 * @returns 可持久化锚点；参考点不参与端点约束。
	 */
	private resolveVertexAnchorRef(
		vertex: RuntimeTopologyVertex | null,
	): string | undefined {
		if (!vertex || vertex.role !== 'topological') {
			return undefined;
		}
		return vertex.ref || vertex.subEntityId || `vertex-index:${vertex.index}`;
	}
}
